'use client';

import { type ComponentProps, type ReactElement } from 'react';
import type { BrandContent } from '@/lib/api/fetch-brand';
import { DynamicImage } from '@/components/dynamic-image';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { useTranslation } from 'react-i18next';
import { SvgRight } from '../icons-eataly';
import { resolveImageAlt } from '@/lib/images/resolve-image-alt';

export type BrandProps = {
    /** Brand custom object content from the category route loader */
    data: BrandContent | null;
    loading?: 'lazy' | 'eager';
} & ComponentProps<'section'>;

/** PLP Brand storytelling block — data comes from the category route loader. */
export default function Brand({ data, loading = 'lazy', className, ...props }: BrandProps): ReactElement | null {
    const { t } = useTranslation('brand');
    const { c_pimBrandDescription, c_image_1, c_pimBrandClaim, c_pimBrandName } = data ?? {};

    const hasBody = Boolean(c_pimBrandDescription?.trim());
    const hasTitleText = Boolean(c_pimBrandClaim?.trim());
    const imageSrc = c_image_1 ? c_image_1 : undefined;
    const hasImage = Boolean(imageSrc);

    // Omit the block when the custom object exists but carries no editorial payload (no orphan chrome).
    if (!hasBody && !hasTitleText && !hasImage) {
        return null;
    }

    return (
        <section className={className} data-component="brand" {...props}>
            <div className=" pb-8 lg:pb-0  grid-global gap-y-6 lg:gap-y-0 bg-neutral-white">
                {hasImage && imageSrc && (
                    <div className="relative -mx-[24px] w-[calc(100%+48px)] lg:-mx-1 lg:w-[calc(100%+8px)] col-span-6 lg:col-span-8 h-full lg:h-[720px] aspect-[4/3] lg:aspect-auto overflow-hidden">
                        <DynamicImage
                            src={imageSrc}
                            alt={resolveImageAlt({ name: c_pimBrandName })}
                            className="h-full w-full"
                            imageProps={{ className: 'block h-full w-full object-cover' }}
                            widths={{ base: '100vw', lg: '66vw' }}
                            loading={loading}
                            priority={loading === 'eager' ? 'high' : undefined}
                        />
                    </div>
                )}

                <div
                    className={cn(
                        'flex flex-col gap-6 justify-center col-span-6',
                        hasImage ? ' lg:col-span-4 lg:ps-10 lg:pe-14 xl:ps-20 xl:pe-30' : 'lg:col-start-4 lg:col-end-10'
                    )}>
                    <div className="flex flex-col gap-2">
                        <p className={cn('label-l text-muted-foreground', !hasImage && 'text-center')}>
                            {t('sectionLabel')}
                        </p>
                        {hasTitleText && (
                            <h2 className={cn(!hasImage && 'mx-auto', 'heading-headline-m text-neutral-10')}>
                                {c_pimBrandClaim}
                            </h2>
                        )}
                    </div>
                    {hasBody && c_pimBrandDescription && (
                        <div
                            className={cn('text-neutral-10 body-copy-l [&_strong]:font-bold line-clamp-6')}
                            // eslint-disable-next-line react/no-danger -- BM/PIM-authored HTML
                            dangerouslySetInnerHTML={{ __html: c_pimBrandDescription }}
                        />
                    )}
                    <Button
                        asChild
                        hierarchy="secondary"
                        iconAfter={<SvgRight />}
                        className={cn(!hasImage && 'mx-auto', 'border-none w-fit p-0 cursor-default')}>
                        <span aria-disabled="true">{t('readMore')}</span>
                    </Button>
                </div>
            </div>
        </section>
    );
}
